import * as React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getOnboardingFunnel } from '../Api';
import type {
    OnboardingAuthFailureRowDto,
    OnboardingAuthMethodRowDto,
    OnboardingAuthEntryRowDto,
    OnboardingHomeActionRowDto,
    OnboardingDropOffDto,
    OnboardingFunnelResponseDto,
    OnboardingFunnelStageDto,
    OnboardingInsightDto,
    OnboardingReturnBucketDto,
    OnboardingSegmentRowDto,
} from '../models/dtos/OnboardingFunnelDto';

type PeriodFilter = 'Today' | 'Yesterday' | 'Week' | 'Month' | 'ThreeMonths';
type OsFilter = 'all' | 'android' | 'ios';

const formatPct = (value: number) => `${(value ?? 0).toFixed(1)}%`;
const formatCount = (value: number) => (value ?? 0).toLocaleString();

const phaseMeta: Record<string, { label: string; color: string }> = {
    start: { label: 'Start', color: '#1565c0' },
    before_login: { label: 'Before login', color: '#0277bd' },
    auth: { label: 'Login / register', color: '#00897b' },
    tour: { label: 'Intro tour', color: '#6a1b9a' },
    first_bet: { label: 'First bet', color: '#ef6c00' },
};

const severityColor = (severity: string): 'info' | 'warning' | 'error' | 'success' => {
    if (severity === 'critical') return 'error';
    if (severity === 'warning') return 'warning';
    if (severity === 'info') return 'info';
    return 'success';
};

function cleanStepLabel(label: string) {
    return label.replace(/^\d+\.\s*/, '');
}

function JourneyStep({
    stage,
    maxUsers,
    isWorstLeak,
}: {
    stage: OnboardingFunnelStageDto;
    maxUsers: number;
    isWorstLeak: boolean;
}) {
    const widthPct = maxUsers > 0 ? Math.max(8, (stage.uniqueUsers / maxUsers) * 100) : 8;
    const phase = phaseMeta[stage.phase] ?? { label: stage.phase, color: '#546e7a' };
    const lost = stage.dropOffFromPrevious > 0;

    return (
        <Box sx={{ position: 'relative', pl: { xs: 0, md: 7 }, mb: 1.5 }}>
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'absolute',
                    left: 0,
                    top: 8,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isWorstLeak ? '#c62828' : phase.color,
                    color: '#fff',
                    fontWeight: 800,
                    boxShadow: isWorstLeak ? '0 0 0 4px rgba(198,40,40,0.18)' : 'none',
                }}
            >
                {stage.stepNumber}
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: isWorstLeak ? 'error.light' : 'divider',
                    bgcolor: isWorstLeak ? 'rgba(198,40,40,0.04)' : 'background.paper',
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                    sx={{ mb: 1 }}
                >
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                label={phase.label}
                                sx={{ bgcolor: `${phase.color}18`, color: phase.color, fontWeight: 700 }}
                            />
                        {stage.isEstimated && (
                            <Chip size="small" label="Incomplete signal" variant="outlined" sx={{ fontWeight: 600 }} />
                        )}
                        {isWorstLeak && (
                            <Chip size="small" color="error" label="Biggest leak" sx={{ fontWeight: 700 }} />
                        )}
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                            {cleanStepLabel(stage.label)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {stage.description}
                        </Typography>
                    </Box>
                    <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.25}>
                        <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>
                            {formatCount(stage.uniqueUsers)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            still here · {formatPct(stage.pctOfCohort)} of all new users
                        </Typography>
                        {stage.stepNumber > 1 && (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: lost ? 'error.main' : 'success.main' }}>
                                {formatPct(stage.pctOfPrevious)} continued from previous step
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                <Box sx={{ height: 14, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}>
                    <Box
                        sx={{
                            height: '100%',
                            width: `${widthPct}%`,
                            borderRadius: 999,
                            bgcolor: isWorstLeak ? '#c62828' : phase.color,
                            transition: 'width 0.35s ease',
                        }}
                    />
                </Box>

                {lost && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'error.main', fontWeight: 600 }}>
                        Lost {formatCount(stage.dropOffFromPrevious)} users before this step
                        ({formatPct(stage.dropOffPctFromPrevious)} of “{cleanStepLabel(stage.previousStageLabel || 'previous')}”)
                    </Typography>
                )}
            </Paper>
        </Box>
    );
}

function DropOffRow({ row, rank }: { row: OnboardingDropOffDto; rank: number }) {
    const phase = phaseMeta[row.phase] ?? { label: row.phase, color: '#546e7a' };
    const isTop = rank === 1;
    return (
        <TableRow sx={{ bgcolor: isTop ? 'rgba(198,40,40,0.06)' : 'transparent' }}>
            <TableCell sx={{ fontWeight: 800, width: 48 }}>#{rank}</TableCell>
            <TableCell>
                <Typography sx={{ fontWeight: 700 }}>
                    {cleanStepLabel(row.fromLabel)} → {cleanStepLabel(row.toLabel)}
                </Typography>
                <Chip
                    size="small"
                    label={phase.label}
                    sx={{ mt: 0.5, bgcolor: `${phase.color}18`, color: phase.color, fontWeight: 600 }}
                />
            </TableCell>
            <TableCell align="right">
                <Typography sx={{ fontWeight: 800, color: 'error.main' }}>
                    −{formatCount(row.usersLost)}
                </Typography>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatPct(row.dropOffPct)}
            </TableCell>
            <TableCell align="right">{formatCount(row.usersRemaining)}</TableCell>
        </TableRow>
    );
}

function InsightCard({ insight }: { insight: OnboardingInsightDto }) {
    return (
        <Alert severity={severityColor(insight.severity)} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {insight.title}
            </Typography>
            <Typography variant="body2">{insight.detail}</Typography>
        </Alert>
    );
}

function SegmentTable({ title, rows }: { title: string; rows: OnboardingSegmentRowDto[] }) {
    const showEarly =
        rows.some((r) => (r.sawSplash ?? 0) > 0 || (r.acceptedPrivacy ?? 0) > 0 || (r.reachedAuth ?? 0) > 0);

    return (
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Counts at each funnel step + where this segment loses users. Red = worst drop for that row.
            </Typography>
            {rows.length === 0 ? (
                <Alert severity="info">No segment data in this period.</Alert>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Segment</TableCell>
                                <TableCell align="right">New</TableCell>
                                {showEarly && (
                                    <>
                                        <TableCell align="right">Splash</TableCell>
                                        <TableCell align="right">Privacy</TableCell>
                                        <TableCell align="right">Auth UI</TableCell>
                                        <TableCell align="right">Signed in</TableCell>
                                    </>
                                )}
                                <TableCell align="right">Tour start</TableCell>
                                <TableCell align="right">Tour done</TableCell>
                                <TableCell align="right">Skipped</TableCell>
                                <TableCell align="right">1st bet</TableCell>
                                <TableCell align="right">Conv.</TableCell>
                                <TableCell align="right">↓ to tour</TableCell>
                                <TableCell align="right">↓ tour finish</TableCell>
                                <TableCell align="right">↓ to 1st bet</TableCell>
                                <TableCell>Worst leak</TableCell>
                                <TableCell align="right">Auth err</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const worst = row.worstDropPct ?? 0;
                                const dropCell = (value: number | undefined, label: string) => {
                                    const v = value ?? 0;
                                    const isWorst = row.worstDropLabel === label && v >= 20;
                                    return (
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: isWorst ? 800 : 400,
                                                color: isWorst ? 'error.main' : 'inherit',
                                                bgcolor: isWorst ? 'rgba(198,40,40,0.06)' : undefined,
                                            }}
                                        >
                                            {formatPct(v)}
                                        </TableCell>
                                    );
                                };
                                return (
                                    <TableRow key={row.key}>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                                        <TableCell align="right">{formatCount(row.newUsers)}</TableCell>
                                        {showEarly && (
                                            <>
                                                <TableCell align="right">{formatCount(row.sawSplash ?? 0)}</TableCell>
                                                <TableCell align="right">{formatCount(row.acceptedPrivacy ?? 0)}</TableCell>
                                                <TableCell align="right">{formatCount(row.reachedAuth ?? 0)}</TableCell>
                                                <TableCell align="right">{formatCount(row.authenticated ?? 0)}</TableCell>
                                            </>
                                        )}
                                        <TableCell align="right">{formatCount(row.startedTour)}</TableCell>
                                        <TableCell align="right">{formatCount(row.completedTour)}</TableCell>
                                        <TableCell align="right">{formatCount(row.abandonedTour ?? 0)}</TableCell>
                                        <TableCell align="right">{formatCount(row.firstBet)}</TableCell>
                                        <TableCell align="right">{formatPct(row.conversionPct)}</TableCell>
                                        {dropCell(row.dropToTourPct, 'New → Tour')}
                                        {dropCell(row.dropTourFinishPct, 'Tour → Finished')}
                                        {dropCell(row.dropToFirstBetPct, 'Finished → First bet')}
                                        <TableCell
                                            sx={{
                                                fontWeight: worst >= 40 ? 800 : 600,
                                                color: worst >= 40 ? 'error.main' : 'text.secondary',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.worstDropLabel
                                                ? `${row.worstDropLabel} (${formatPct(worst)})`
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">{formatCount(row.authFailures)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Paper>
    );
}

function Kpi({
    label,
    value,
    sub,
    color,
}: {
    label: string;
    value: string;
    sub: string;
    color: string;
}) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: `5px solid ${color}`,
            }}
        >
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, my: 0.5 }}>
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {sub}
            </Typography>
        </Paper>
    );
}

export default function OnboardingFunnelPage() {
    const [period, setPeriod] = React.useState<PeriodFilter>('Week');
    const [osType, setOsType] = React.useState<OsFilter>('all');
    const [countryCode, setCountryCode] = React.useState<string>('all');
    const [lang, setLang] = React.useState<string>('all');
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [data, setData] = React.useState<OnboardingFunnelResponseDto | null>(null);

    React.useEffect(() => {
        let active = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            const result = await getOnboardingFunnel(
                period,
                osType === 'all' ? undefined : osType,
                countryCode === 'all' ? undefined : countryCode,
                lang === 'all' ? undefined : lang,
            );
            if (!active) return;
            if (!result) {
                setError('Could not load onboarding funnel stats.');
                setData(null);
            } else {
                setData(result);
            }
            setLoading(false);
        };
        void load();
        return () => {
            active = false;
        };
    }, [period, osType, countryCode, lang]);

    const maxUsers = React.useMemo(() => {
        if (!data) return 1;
        return Math.max(data.cohortUsers, ...data.stages.map((s) => s.uniqueUsers), 1);
    }, [data]);

    const worstLeakId = data?.dropOffRanking?.[0]?.toStageId ?? null;
    const countryOptions = data?.byCountry?.map((r) => r.key).filter((k) => k !== 'unknown') ?? [];
    const langOptions = data?.byLanguage?.map((r) => r.key).filter((k) => k !== 'unknown') ?? [];

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'flex-end' }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900 }}>
                            Onboarding funnel
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
                            Follow new users from the first open to the first real bet.
                            Filter by country / language to see where each segment gets stuck.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Period</InputLabel>
                            <Select
                                label="Period"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value as PeriodFilter)}
                            >
                                <MenuItem value="Today">Today</MenuItem>
                                <MenuItem value="Yesterday">Yesterday</MenuItem>
                                <MenuItem value="Week">Last 7 days</MenuItem>
                                <MenuItem value="Month">Last 30 days</MenuItem>
                                <MenuItem value="ThreeMonths">Last 90 days</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Platform</InputLabel>
                            <Select
                                label="Platform"
                                value={osType}
                                onChange={(e) => setOsType(e.target.value as OsFilter)}
                            >
                                <MenuItem value="all">All platforms</MenuItem>
                                <MenuItem value="android">Android</MenuItem>
                                <MenuItem value="ios">iOS</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Country</InputLabel>
                            <Select
                                label="Country"
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                            >
                                <MenuItem value="all">All</MenuItem>
                                {countryOptions.map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Language</InputLabel>
                            <Select
                                label="Language"
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                            >
                                <MenuItem value="all">All</MenuItem>
                                {langOptions.map((l) => (
                                    <MenuItem key={l} value={l}>{l}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>

                {loading && (
                    <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && error && <Alert severity="error">{error}</Alert>}

                {!loading && data && (
                    <>
                        {data.dataQualityNote && (
                            <Alert severity="warning">
                                <strong>How to read this data:</strong> {data.dataQualityNote}
                            </Alert>
                        )}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                background:
                                    'linear-gradient(135deg, rgba(21,101,192,0.10) 0%, rgba(0,137,123,0.08) 50%, rgba(239,108,0,0.06) 100%)',
                            }}
                        >
                            <Typography variant="overline" sx={{ fontWeight: 700 }}>
                                Plain-language summary
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, mb: 1.5, maxWidth: 980 }}>
                                {data.verdict}
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            New accounts → first bet
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                            {formatPct(data.overallConversionPct)}
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(100, data.overallConversionPct)}
                                        sx={{ height: 12, borderRadius: 999 }}
                                    />
                                </Box>
                                {data.dropOffRanking[0] && (
                                    <Alert severity={data.biggestDropOffPct >= 40 ? 'error' : 'warning'} sx={{ minWidth: 280 }}>
                                        <strong>Start fixing:</strong>{' '}
                                        {cleanStepLabel(data.dropOffRanking[0].fromLabel)} →{' '}
                                        {cleanStepLabel(data.dropOffRanking[0].toLabel)}
                                    </Alert>
                                )}
                            </Stack>
                        </Paper>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={2}>
                                <Kpi
                                    label="New accounts"
                                    value={formatCount(data.summary.newUsers)}
                                    sub="Created in this period (not returning logins)"
                                    color="#1565c0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Kpi
                                    label="Auth success"
                                    value={formatCount(data.summary.authenticated)}
                                    sub={
                                        (data.authEntries?.length ?? 0) > 0
                                            ? [
                                                  data.summary.guestEntries
                                                      ? `${formatCount(data.summary.guestEntries ?? 0)} guest`
                                                      : null,
                                                  data.summary.registerEntries
                                                      ? `${formatCount(data.summary.registerEntries ?? 0)} register`
                                                      : null,
                                                  data.summary.loginEntries
                                                      ? `${formatCount(data.summary.loginEntries ?? 0)} login`
                                                      : null,
                                              ]
                                                  .filter(Boolean)
                                                  .join(' · ') || 'See Guest / Register / Login below'
                                            : `${formatCount(data.summary.guestLogins)} guest · returning logins excluded`
                                    }
                                    color="#00897b"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Kpi
                                    label="Finished tour"
                                    value={formatCount(data.summary.completedTour)}
                                    sub={`${formatCount(data.summary.abandonedTour)} skipped the tour`}
                                    color="#6a1b9a"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Kpi
                                    label="First bet"
                                    value={formatCount(data.summary.placedFirstBet)}
                                    sub={`${formatPct(data.overallConversionPct)} of new users`}
                                    color="#ef6c00"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Kpi
                                    label="Auth failures"
                                    value={formatCount(data.summary.authFailures ?? 0)}
                                    sub="Users with login errors"
                                    color="#c62828"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Kpi
                                    label="Came back (other day)"
                                    value={
                                        data.returnRetention
                                            ? formatCount(data.returnRetention.returnedUsers)
                                            : '—'
                                    }
                                    sub={
                                        data.returnRetention
                                            ? `${formatPct(data.returnRetention.returnedPct)} of new · avg ${data.returnRetention.avgDaysBetweenCreateAndLastActive.toFixed(1)}d later`
                                            : 'createdAt vs lastActiveAt'
                                    }
                                    color="#4527a0"
                                />
                            </Grid>
                        </Grid>

                        {data.returnRetention && (
                            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                    Return after signup
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    New accounts in this period. We count a return when{' '}
                                    <strong>lastActiveAt</strong> is on a <strong>later calendar day</strong> than{' '}
                                    <strong>createdAt</strong>. Days = create → last activity (not first reopen).
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="overline" color="text.secondary">
                                            New accounts
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                            {formatCount(data.returnRetention.cohortUsers)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="overline" color="text.secondary">
                                            Returned (other day)
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#4527a0' }}>
                                            {formatCount(data.returnRetention.returnedUsers)}{' '}
                                            <Typography component="span" variant="body2" color="text.secondary">
                                                ({formatPct(data.returnRetention.returnedPct)})
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="overline" color="text.secondary">
                                            Avg days create → last active
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                            {data.returnRetention.avgDaysBetweenCreateAndLastActive.toFixed(1)}d
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When they came back</TableCell>
                                            <TableCell align="right">Users</TableCell>
                                            <TableCell align="right">Share</TableCell>
                                            <TableCell sx={{ width: '40%' }}> </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.returnRetention.buckets.map((row: OnboardingReturnBucketDto) => (
                                            <TableRow key={row.id}>
                                                <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                                                <TableCell align="right">{formatCount(row.uniqueUsers)}</TableCell>
                                                <TableCell align="right">{formatPct(row.pctOfCohort)}</TableCell>
                                                <TableCell>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(100, row.pctOfCohort)}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 1,
                                                            bgcolor: 'action.hover',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor:
                                                                    row.id === 'same_day' ? '#90a4ae' : '#4527a0',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                    {data.returnRetention.note}
                                </Typography>
                            </Paper>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={7}>
                                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                        The journey (step by step)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                                        Read top → bottom. Each card = how many users reached that step.
                                        Red = largest drop from the previous step.
                                    </Typography>
                                    {data.stages.map((stage) => (
                                        <JourneyStep
                                            key={stage.id}
                                            stage={stage}
                                            maxUsers={maxUsers}
                                            isWorstLeak={stage.id === worstLeakId}
                                        />
                                    ))}
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Stack spacing={2}>
                                    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                            Where do we lose people?
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Ranked by users lost. #1 is the first place to investigate.
                                        </Typography>
                                        {data.dropOffRanking.length === 0 ? (
                                            <Alert severity="success">No meaningful drop-offs in this period.</Alert>
                                        ) : (
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>#</TableCell>
                                                        <TableCell>Between steps</TableCell>
                                                        <TableCell align="right">Lost</TableCell>
                                                        <TableCell align="right">%</TableCell>
                                                        <TableCell align="right">Left</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data.dropOffRanking.map((row, idx) => (
                                                        <DropOffRow key={`${row.fromStageId}-${row.toStageId}`} row={row} rank={idx + 1} />
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </Paper>

                                    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>
                                            What to do next
                                        </Typography>
                                        <Stack spacing={1.25}>
                                            {data.insights.map((insight, idx) => (
                                                <InsightCard key={`${insight.title}-${idx}`} insight={insight} />
                                            ))}
                                        </Stack>
                                    </Paper>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <SegmentTable title="By country (each funnel step)" rows={data.byCountry ?? []} />
                            </Grid>
                            <Grid item xs={12}>
                                <SegmentTable title="By language (each funnel step)" rows={data.byLanguage ?? []} />
                            </Grid>
                        </Grid>

                        {(data.byCountry?.length ?? 0) > 0 && (
                            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                    Countries dropping hardest
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Sorted by worst step-to-step loss (min. 50 new accounts). Shows if one country
                                    abandons earlier than others.
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell align="right">New</TableCell>
                                            <TableCell>Worst leak</TableCell>
                                            <TableCell align="right">Drop</TableCell>
                                            <TableCell align="right">1st bet conv.</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...(data.byCountry ?? [])]
                                            .filter((r) => r.newUsers >= 50)
                                            .sort((a, b) => (b.worstDropPct ?? 0) - (a.worstDropPct ?? 0))
                                            .slice(0, 10)
                                            .map((row) => (
                                                <TableRow key={`leak-${row.key}`}>
                                                    <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                                                    <TableCell align="right">{formatCount(row.newUsers)}</TableCell>
                                                    <TableCell>{row.worstDropLabel ?? '—'}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: 800,
                                                            color:
                                                                (row.worstDropPct ?? 0) >= 40
                                                                    ? 'error.main'
                                                                    : 'inherit',
                                                        }}
                                                    >
                                                        {formatPct(row.worstDropPct ?? 0)}
                                                    </TableCell>
                                                    <TableCell align="right">{formatPct(row.conversionPct)}</TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}

                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                Auth success split (one step, three ways in)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Same funnel step (“Entered”), split by how they authenticated.
                                Guest = new anonymous account from the login/guest screen (still a registration).
                                Register = new email/social account. Log in = returning account.
                            </Typography>
                            {(data.authEntries?.length ?? 0) === 0 ? (
                                <Alert severity="info">
                                    Needs the Android build that sends SignIn.entry_kind (guest / register / login).
                                </Alert>
                            ) : (
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    {(data.authEntries ?? []).map((row: OnboardingAuthEntryRowDto) => (
                                        <Grid item xs={12} sm={4} key={row.entryKind}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderLeft: '5px solid',
                                                    borderLeftColor:
                                                        row.entryKind === 'guest'
                                                            ? '#00897b'
                                                            : row.entryKind === 'register'
                                                              ? '#1565c0'
                                                              : '#6a1b9a',
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography variant="overline" color="text.secondary">
                                                    {row.label}
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 900, my: 0.5 }}>
                                                    {formatCount(row.uniqueUsers)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatPct(row.pctOfAuthStep)} of auth successes
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                    {row.description}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                Provider (method) for first-time entries
                            </Typography>
                            {(data.authMethods?.length ?? 0) === 0 ? (
                                <Alert severity="info">
                                    No SignIn events with method yet. After the Android update, this table fills in.
                                </Alert>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Method</TableCell>
                                            <TableCell align="right">Users</TableCell>
                                            <TableCell align="right">Share</TableCell>
                                            <TableCell sx={{ width: '40%' }}> </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.authMethods.map((row: OnboardingAuthMethodRowDto) => (
                                            <TableRow key={row.method}>
                                                <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                                                <TableCell align="right">{formatCount(row.uniqueUsers)}</TableCell>
                                                <TableCell align="right">{formatPct(row.pctOfAuthenticated)}</TableCell>
                                                <TableCell>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(100, row.pctOfAuthenticated)}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 1,
                                                            bgcolor: 'action.hover',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor:
                                                                    row.method === 'unknown' ? '#90a4ae' : '#00897b',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                Home taps (after tour)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                What users tap on the Home screen outside the intro tour (battles, offers, matches…).
                                Needs the Android build that sends HomeAction events.
                            </Typography>
                            {(data.homeActions?.length ?? 0) === 0 ? (
                                <Alert severity="info">
                                    No HomeAction events yet. After the Android update, post-tour Home taps appear here.
                                </Alert>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Action</TableCell>
                                            <TableCell align="right">Users</TableCell>
                                            <TableCell align="right">Taps</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.homeActions ?? []).map((row: OnboardingHomeActionRowDto) => (
                                            <TableRow key={row.action}>
                                                <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                                                <TableCell align="right">{formatCount(row.uniqueUsers)}</TableCell>
                                                <TableCell align="right">{formatCount(row.events)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                Auth failures (request / login errors)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Concrete failures on login/register (provider + stage + reason).
                                Needs the Android build that dual-writes AuthFailure.
                            </Typography>
                            {(data.authFailures?.length ?? 0) === 0 ? (
                                <Alert severity="info">
                                    No AuthFailure events yet. After the Android update, failed Google/email/guest
                                    attempts will appear here.
                                </Alert>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Provider</TableCell>
                                            <TableCell>Stage</TableCell>
                                            <TableCell>Reason</TableCell>
                                            <TableCell align="right">Users</TableCell>
                                            <TableCell align="right">Events</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.authFailures.map((row, idx) => (
                                            <TableRow key={`${row.provider}-${row.stage}-${idx}`}>
                                                <TableCell sx={{ fontWeight: 700 }}>{row.provider}</TableCell>
                                                <TableCell>{row.stage}</TableCell>
                                                <TableCell>{row.reason}</TableCell>
                                                <TableCell align="right">{formatCount(row.uniqueUsers)}</TableCell>
                                                <TableCell align="right">{formatCount(row.events)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Paper>

                        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                    <Typography sx={{ fontWeight: 800 }}>Tour screens in detail</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Open this when you want to see exactly which intro-tour screen loses users
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {data.tourSteps.length === 0 ? (
                                    <Alert severity="info">No tour step events in this period yet.</Alert>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {data.tourSteps.map((stage, index) => {
                                            const prev = index === 0 ? null : data.tourSteps[index - 1];
                                            const width = Math.max(
                                                6,
                                                (stage.uniqueUsers / Math.max(...data.tourSteps.map((s) => s.uniqueUsers), 1)) * 100
                                            );
                                            const bad = stage.dropOffPctFromPrevious >= 25 && stage.dropOffFromPrevious > 0;
                                            return (
                                                <Box key={stage.id}>
                                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                                        <Typography sx={{ fontWeight: 700 }}>{stage.label}</Typography>
                                                        <Typography sx={{ fontWeight: 800 }}>
                                                            {formatCount(stage.uniqueUsers)}
                                                            <Typography component="span" variant="body2" color="text.secondary">
                                                                {' '}
                                                                ({formatPct(stage.pctOfPrevious)} of previous)
                                                            </Typography>
                                                        </Typography>
                                                    </Stack>
                                                    <Box sx={{ height: 10, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                                        <Box
                                                            sx={{
                                                                width: `${width}%`,
                                                                height: '100%',
                                                                bgcolor: bad ? '#c62828' : '#6a1b9a',
                                                            }}
                                                        />
                                                    </Box>
                                                    {prev && stage.dropOffFromPrevious > 0 && (
                                                        <Typography variant="caption" color={bad ? 'error.main' : 'text.secondary'}>
                                                            −{formatCount(stage.dropOffFromPrevious)} from “{cleanStepLabel(prev.label)}”
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                    <Typography sx={{ fontWeight: 800 }}>Auth button clicks</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Which buttons people tap on login / register (needs the new Android build)
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {data.authClicks.length === 0 ? (
                                    <Alert severity="info">
                                        No auth click events yet. They appear after the updated Android build starts reporting.
                                    </Alert>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Button</TableCell>
                                                <TableCell align="right">Unique users</TableCell>
                                                <TableCell align="right">Share of auth screen</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.authClicks.map((row) => (
                                                <TableRow key={row.id}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                                                    <TableCell align="right">{formatCount(row.uniqueUsers)}</TableCell>
                                                    <TableCell align="right">{formatPct(row.pctOfCohort)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Alert severity="info">
                            <strong>How to read this:</strong> start from “New accounts”, then go down.
                            Returning logins are not part of this funnel.
                            If a step keeps most people, it is healthy. If many leave between two steps, that gap is your problem.
                            Tour + first bet work today; splash / privacy / auth clicks fill in after the new Android release.
                        </Alert>
                    </>
                )}
            </Stack>
        </Box>
    );
}
